import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API } from "@/lib/api";
import { VolunteerFilterBar } from "@/components/admin/VolunteerFilterBar";

export default function AdminVolunteers() {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expertise, setExpertise] = useState("all");

  useEffect(() => {
    api.get("/admin/volunteers").then(({ data }) => {
      setVolunteers(data);
      setLoading(false);
    });
  }, []);

  const handleExport = () => {
    window.open(`${API}/admin/volunteers/export`, "_blank");
  };

  const filtered = volunteers.filter((v) => {
    const matchesExpertise = expertise === "all" || (v.help_types || []).includes(expertise);
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || v.name.toLowerCase().includes(q) || v.email.toLowerCase().includes(q);
    return matchesExpertise && matchesSearch;
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Admin</p>
          <h1 className="font-display font-extrabold uppercase text-2xl md:text-3xl text-zinc-950 tracking-tight">Volunteers ({filtered.length}/{volunteers.length})</h1>
        </div>
        <Button onClick={handleExport} data-testid="admin-volunteers-export-button" className="bg-zinc-950 text-white rounded-none hover:bg-zinc-900 hover:shadow-[0px_4px_0px_0px_#4FD1C5]">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-14"><div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-950 rounded-full animate-spin" /></div>
      ) : (
        <>
          <VolunteerFilterBar search={search} onSearchChange={setSearch} expertise={expertise} onExpertiseChange={setExpertise} />
          <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto" data-testid="admin-volunteers-table">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-200 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500">Email</TableHead>
                  <TableHead className="text-zinc-500">Phone</TableHead>
                  <TableHead className="text-zinc-500">Location</TableHead>
                  <TableHead className="text-zinc-500">Profession</TableHead>
                  <TableHead className="text-zinc-500">Ways to Help</TableHead>
                  <TableHead className="text-zinc-500">Message</TableHead>
                  <TableHead className="text-zinc-500">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => (
                  <TableRow key={v.id} data-testid={`admin-volunteer-row-${v.id}`} className="border-zinc-200">
                    <TableCell className="text-zinc-950 font-medium">{v.name}</TableCell>
                    <TableCell className="text-zinc-600">{v.email}</TableCell>
                    <TableCell className="text-zinc-600">{v.phone || "—"}</TableCell>
                    <TableCell className="text-zinc-600">{v.location || "—"}</TableCell>
                    <TableCell className="text-zinc-600">{v.profession || "—"}</TableCell>
                    <TableCell className="text-zinc-600 capitalize max-w-[220px]">
                      {(v.help_types || []).map((h) => h.replace("_", " ")).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="text-zinc-600 max-w-[220px] truncate" title={v.message || ""}>{v.message || "—"}</TableCell>
                    <TableCell className="text-zinc-500">{new Date(v.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filtered.length === 0 && <p className="text-center text-zinc-500 py-10">{volunteers.length === 0 ? "No volunteers yet." : "No volunteers match your filters."}</p>}
          </div>
        </>
      )}
    </div>
  );
}
